import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTimer } from '../context/TimerContext';
import { useCart } from '../context/CartContext';
import { shopifyApi } from '../api/shopify';
import { CountdownTimer } from '../components/CountdownTimer';

export function CartPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isSiteActive, isExpired } = useTimer();
  const { items, removeFromCart, updateQuantity, clearCart, getTotal } = useCart();

  const [checkingOut, setCheckingOut] = useState(false);
  const [error, setError] = useState('');

  // Handle site expiration
  React.useEffect(() => {
    if (isExpired || !isSiteActive) {
      logout();
      navigate('/');
    }
  }, [isExpired, isSiteActive, logout, navigate]);

  const handleCheckout = async () => {
    if (items.length === 0) {
      setError('Your cart is empty');
      return;
    }

    setError('');
    setCheckingOut(true);

    try {
      const cartItems = items.map((item) => ({
        variantId: item.product.variantId,
        quantity: item.quantity,
      }));

      const checkoutUrl = await shopifyApi.createCart(cartItems);
      clearCart();
      window.location.href = checkoutUrl;
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to create checkout');
      setCheckingOut(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const subtotal = getTotal();
  const discount = subtotal * 0.05; // 5% VIP discount
  const total = subtotal - discount;

  return (
    <div className="cart-page">
      <header className="products-header">
        <div className="header-left">
          <Link to="/products">
            <img src="/JackpointLogo.avif" alt="Jackpoint" className="header-logo" />
          </Link>
          <p className="user-email">{user?.email}</p>
        </div>
        <div className="header-right">
          <CountdownTimer />
          <button onClick={handleLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </header>

      <main className="cart-main">
        <div className="breadcrumb">
          <Link to="/products">← Back to Products</Link>
        </div>

        <h1 className="page-title">Your Cart</h1>

        {error && <div className="error-message">{error}</div>}

        {items.length === 0 ? (
          <div className="cart-empty">
            <p>Your cart is empty</p>
            <Link to="/products" className="continue-shopping-btn">
              Continue Shopping
            </Link>
          </div>
        ) : (
          <div className="cart-content">
            <div className="cart-items">
              {items.map((item) => (
                <div key={item.product.id} className="cart-item">
                  <div className="cart-item-image">
                    {item.product.imageUrl ? (
                      <img src={item.product.imageUrl} alt={item.product.title} />
                    ) : (
                      <div className="no-image">No image</div>
                    )}
                  </div>
                  <div className="cart-item-details">
                    <h3 className="cart-item-title">{item.product.title}</h3>
                    <p className="cart-item-price">
                      {formatPrice(parseFloat(item.product.price))} each
                    </p>
                  </div>
                  <div className="cart-item-quantity">
                    <button
                      onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                    >
                      -
                    </button>
                    <span>{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                    >
                      +
                    </button>
                  </div>
                  <div className="cart-item-total">
                    {formatPrice(parseFloat(item.product.price) * item.quantity)}
                  </div>
                  <button
                    className="cart-item-remove"
                    onClick={() => removeFromCart(item.product.id)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            <div className="cart-summary">
              <h3>Order Summary</h3>
              <div className="summary-row">
                <span>Subtotal</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              <div className="summary-row discount">
                <span>VIP Discount (5%)</span>
                <span>-{formatPrice(discount)}</span>
              </div>
              <div className="summary-row total">
                <span>Total</span>
                <span>{formatPrice(total)}</span>
              </div>
              <button
                className="checkout-btn"
                onClick={handleCheckout}
                disabled={checkingOut || items.length === 0}
              >
                {checkingOut ? 'Processing...' : 'Proceed to Checkout'}
              </button>
              <Link to="/products" className="continue-shopping-link">
                Continue Shopping
              </Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default CartPage;
